import Immutable from 'immutable';
import dayjs, { Dayjs } from 'dayjs';
import { presets } from '../../components/WeekAvailability';
import {isValidPhoneNumber, sundayFirstToMondayFirst} from '../functions';
import validator from 'validator';
import { apiBase } from '../../env';

const ContactModel = Immutable.Record({
	id: 0,
	name: '',
	phone: '+',
	deviceId: 0,
	email: '',
	function: '',
	remark: '',
	availability: [],
	accountId: 0,
	oooList: [],
	photoUrl: null
});

export default class Contact extends ContactModel {
	id: number;
	name: string;
	phone: string;
	deviceId: number;
	email: string;
	function: string;
	remark: string;
	oooList: any[];
	availability: boolean[][];
	photoUrl: string;

	accountId: number;

	constructor(props) {
		props.availability = props.availability || presets.never;
		super(props);
	}

	isAvailable(date: dayjs) {
		if(!date) date = dayjs();

		if(this.get('oooList').find(o => date.unix() > o.from && date.unix() < o.to)) 
			return false;
		const slot = (date.hour()*2) + (date.minute() > 30 ? 1 : 0);
		return this.isAvailableAtSlot(sundayFirstToMondayFirst(date.day()), slot);
	}

	/**
	 * Monday = 0
	 * @param day
	 * @param slot
	 * @returns {*}
	 */
	isAvailableAtSlot(day: number, slot: number) {
		const dayAvailability = this.get('availability')[day];
		return dayAvailability[slot];
	}

	/**
	 * Available on the given date when at least one slot of that weekday is available
	 * and the contact is not out of office for the entire day.
	 */
	isAvailableOnDate(date: Dayjs) {
		const weekday = this.get('availability')[sundayFirstToMondayFirst(date.day())];
		if (!weekday?.some(Boolean)) return false;
		const start = date.startOf('day').unix();
		const nextStart = date.add(1, 'day').startOf('day').unix();
		// The OOO editor stores `to` as end of day minus one minute
		return !(this.get('oooList') || []).some(o => o.from <= start && o.to >= nextStart - 60);
	}

	/**
	 * Available time ranges on a date, in minutes since midnight, with out-of-office time cut out
	 * @returns {{start: number, end: number}[]}
	 */
	availableRangesOnDate(date: Dayjs) {
		const weekday = this.get('availability')[sundayFirstToMondayFirst(date.day())] || [];
		let ranges = [];
		weekday.forEach((available, slot) => {
			if (!available) return;
			const last = ranges[ranges.length - 1];
			if (last && last.end === slot * 30) last.end += 30;
			else ranges.push({start: slot * 30, end: slot * 30 + 30});
		});

		const dayStart = date.startOf('day');
		const dayLength = date.add(1, 'day').startOf('day').diff(dayStart, 'minute');
		(this.get('oooList') || []).forEach(o => {
			const from = Math.max(0, dayjs.unix(o.from).diff(dayStart, 'minute'));
			// The OOO editor stores `to` as end of day minus one minute
			const toMinute = dayjs.unix(o.to).diff(dayStart, 'minute');
			const to = Math.min(dayLength, toMinute >= dayLength - 1 ? dayLength : toMinute);
			if (to <= from) return;
			ranges = ranges.flatMap(r => {
				if (to <= r.start || from >= r.end) return [r];
				const parts = [];
				if (from > r.start) parts.push({start: r.start, end: from});
				if (to < r.end) parts.push({start: to, end: r.end});
				return parts;
			});
		});
		return ranges;
	}

	hasOOOAtDate(date: Dayjs) {
		return this.get('ooo') && date.isBetween(this.oooFromDate, this.oooToDate);
	}
	hasOOONowOrSoon() {
		return this.get('ooo') && this.oooFromDate.isBefore(dayjs().add(1, 'week')) && this.oooToDate.isAfter(dayjs());
	}

	isAlwaysAvailable() {
		return this.get('availability').flat().indexOf(false) === -1;
	}

	clone() {
		return this.set('id', this.id);
	}
	
	isValid() {
		if(this.name?.length < 3) return false;
		if(this.hasPhone && !isValidPhoneNumber(this.phone)) return false;
		if(this.email && !validator.isEmail(this.email)) return false;
		return !!this.email || this.hasPhone;
	}
	
	/** The photo as an image source, or null; the API returns its path relative to the API base. */
	get photoSrc() {
		return this.photoUrl ? `${apiBase()}${this.photoUrl}` : null;
	}

	get hasPhone() {
		return this.phone?.length >= 3;
	}

	static create() {
		const now = dayjs();
		return new Contact({
			availability: presets.always,
			oooFrom: now.unix(),
			oooTo: now.add(1, 'day').unix()
		});
	}

	static mergeAvailability(contacts: Contact[]) {
		const availability = presets.never;
		availability.forEach((dayAvailability, day) => {
			dayAvailability.forEach((available, slot) => {
				availability[day][slot] = contacts.filter(g => g && g.isAvailableAtSlot(day, slot)).length;
			});
		});
		return availability;
	}
}
